import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useConnectionAdapter } from "../../../services/ConnectionRegistryProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";
import { useMyQueueClient } from "./useMyQueueClient";
import type {
  MyQueueFilterModel,
  MyQueueInvoiceData,
  MyQueueLocation,
  MyQueueProdTaskData,
  MyQueueQcInfoData,
  MyQueueQcProdTask,
  MyQueueTask,
  MyQueueTaskAction,
  MyQueueTaskDetailData,
  MyQueueTaskSearchRequest,
  MyQueueTimeclockAction,
  MyQueueTimeclockData,
} from "../services/myqueueApi";
import { ACTION_LABELS, resolveState, stateAfterAction } from "../services/timeclockState";
import { readMigratedSessionPref } from "../../../shared/sessionPref";
import { createPollBackoff } from "../../../shared/api/pollBackoff";

// AIVA-6685 (V-08): these UI preferences live in sessionStorage, not
// localStorage — see shared/sessionPref.ts. They are taskpane-only, so a
// per-window store is safe; a legacy localStorage value is migrated once.
const SITE_STORAGE_KEY = "aiva_myqueue_site";
const LOCATION_STORAGE_KEY = "aiva_myqueue_location_id";
const PAGE_SIZE = 20;
const POLL_INTERVAL_MS = 30_000;

/**
 * Report a MyQueue failure through the shared connector telemetry pipeline.
 * The transport layer only logs HTTP status; this adds a connector-tagged
 * error.connector.failure so non-HTTP failures (parse, business logic, thrown
 * after a 2xx) and the connector attribution are no longer lost.
 */
function reportMyQueueError(reason: string, e: unknown): void {
  logConnectorFailure("myqueue", e, reason);
}

/**
 * The success half of {@link reportMyQueueError}. AIVA-6494: without it every
 * `myqueue` row carrying a connectorSystemName is a failure, so Connector
 * reliability divides failures by failures and reports 0.0% — measured, not
 * hypothetical. The reason stem matches its failure sibling.
 */
function reportMyQueueOk(reason: string): void {
  logConnectorSuccess("myqueue", reason);
}

/**
 * Turn a MyQueue failure into a user-facing message. Transport errors arrive as
 * `MyQueue POST /path: 400 {json}` — we pull the JSON tail and surface the
 * backend's `detailedMessages` (the actionable reasons, e.g. "The task has not
 * been completed yet.") or its `message`, never the raw URL + status code.
 * Clean messages with no JSON body (e.g. a 200 with success:false) pass through.
 */
function friendlyMyQueueError(raw: string, fallback: string): string {
  const brace = raw.indexOf("{");
  // No JSON envelope → the message is already clean (e.g. a 200 with
  // success:false → "rejected"), so surface it as-is.
  if (brace < 0) return raw || fallback;
  try {
    const parsed = JSON.parse(raw.slice(brace)) as {
      message?: string;
      detailedMessages?: string[];
    };
    const details = (parsed.detailedMessages ?? []).filter(Boolean);
    if (details.length > 0) return details.join(" ");
    if (parsed.message) return parsed.message;
  } catch {
    // Malformed body — fall through to the generic fallback.
  }
  // There was a transport envelope but nothing usable in it → never show the
  // raw "MyQueue POST …: 400 {…}" string.
  return fallback;
}

export type MyQueueFilter = "all" | "scheduled" | "active" | "completed";

function buildFilterModel(filter: MyQueueFilter): MyQueueFilterModel {
  if (filter === "scheduled") {
    return {
      started: { filterType: "boolean", type: "equals", filter: false },
      completed: { filterType: "boolean", type: "equals", filter: false },
    };
  }
  if (filter === "active") {
    return {
      started: { filterType: "boolean", type: "equals", filter: true },
      completed: { filterType: "boolean", type: "equals", filter: false },
    };
  }
  if (filter === "completed") {
    return {
      completed: { filterType: "boolean", type: "equals", filter: true },
    };
  }
  return {};
}

function applyClientFilter(tasks: MyQueueTask[], filter: MyQueueFilter): MyQueueTask[] {
  if (filter === "scheduled") return tasks.filter((t) => !t.started && !t.completed);
  if (filter === "active") return tasks.filter((t) => t.started && !t.completed);
  if (filter === "completed") return tasks.filter((t) => t.completed);
  return tasks;
}

export function useMyQueueTasks() {
  const adapter = useConnectionAdapter("myqueue");
  const client = useMyQueueClient();

  const [connected, setConnected] = useState<boolean>(adapter?.isConnected() ?? false);
  const [site, setSite] = useState<string>(
    () => readMigratedSessionPref(SITE_STORAGE_KEY) ?? "pres",
  );
  const [filter, setFilter] = useState<MyQueueFilter>("all");
  const [page, setPage] = useState<number>(1);
  const [tasks, setTasks] = useState<MyQueueTask[]>([]);
  const [inProgressTasks, setInProgressTasks] = useState<MyQueueTask[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const knownIdsRef = useRef<Set<string>>(new Set());
  // Slows the 30s poll after repeated 4xx, e.g. a user without access.
  const [pollBackoff] = useState(createPollBackoff);

  useEffect(() => {
    if (!adapter) {
      setConnected(false);
      return;
    }
    setConnected(adapter.isConnected());
    return adapter.onTokenChange((t) => setConnected(!!t));
  }, [adapter]);

  useEffect(() => {
    try {
      globalThis.sessionStorage?.setItem(SITE_STORAGE_KEY, site);
    } catch {
      // ignore
    }
  }, [site]);

  const load = useCallback(async (opts?: { background?: boolean }) => {
    if (!connected) return;
    // A foreground load (filter/site/page change or first load) clears the
    // current list so the skeleton shows instead of the previous selection's
    // rows lingering — or briefly flashing the empty state — while the new
    // page is fetched. The 30s background poll passes { background: true } so
    // the visible list stays put and updates in place with no flicker.
    if (!opts?.background) {
      setTasks([]);
      setInProgressTasks([]);
      setTotalCount(0);
    }
    setLoading(true);
    setError(null);
    try {
      const req: MyQueueTaskSearchRequest = {
        FilterColumn: "",
        FilterValue: "",
        IsAsc: false,
        PageNum: page,
        PageSize: PAGE_SIZE,
        Sites: site ? [site] : ["pres"],
        SortColumn: "dueDate",
        filterModel: buildFilterModel(filter),
        sortModel: [{ sort: "asc", colId: "dueDate" }],
        startRow: (page - 1) * PAGE_SIZE,
        endRow: page * PAGE_SIZE,
      };
      const result = await client.searchTasks(req);
      if (!result?.success) {
        throw new Error(result?.message ?? "Failed to load tasks");
      }
      const data = result.data ?? {};
      const allTasks = data.tasks ?? [];
      const filtered = applyClientFilter(allTasks, filter);
      setTasks(filtered);
      setInProgressTasks(data.inProgressTasks ?? []);
      // `data.count` is the server's filter-aware total (the filterModel is
      // sent with the request), so it drives pagination for every filter.
      // Using the client-filtered page length here previously collapsed
      // Scheduled/Active/Completed to a single page. `applyClientFilter`
      // stays as a display guard for rows the server filter may not honour.
      setTotalCount(data.count ?? allTasks.length);
      knownIdsRef.current = new Set(allTasks.map((t) => String(t.id)));
      pollBackoff.recordSuccess();
      reportMyQueueOk("load-tasks");
    } catch (e) {
      pollBackoff.recordFailure(e);
      const msg = e instanceof Error ? e.message : "Failed to load tasks";
      setError(msg);
      reportMyQueueError("load-tasks", e);
    } finally {
      setLoading(false);
    }
  }, [client, connected, filter, page, site, pollBackoff]);

  useEffect(() => {
    if (connected) {
      load().catch(() => undefined);
    }
  }, [connected, load]);

  /* Poll for new tasks every 30s while connected */
  useEffect(() => {
    if (!connected) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => {
      // Only timer ticks honour the back-off; a filter change or Refresh always loads.
      if (pollBackoff.shouldSkip()) return;
      load({ background: true }).catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [connected, load, pollBackoff]);

  const refresh = useCallback(() => load(), [load]);

  return {
    connected,
    site,
    setSite,
    filter,
    setFilter,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    tasks,
    inProgressTasks,
    totalCount,
    loading,
    error,
    refresh,
  };
}

export function useMyQueueTaskDetail() {
  const client = useMyQueueClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MyQueueTaskDetailData | null>(null);

  const fetchDetails = useCallback(
    async (workflowId: number) => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const resp = await client.getTaskDetails(workflowId);
        setData(resp?.data ?? null);
        reportMyQueueOk("load-task-details");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load task details";
        setError(msg);
        reportMyQueueError("load-task-details", e);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const updateComment = useCallback(
    async (workflowId: number, comments: string) => {
      const result = await client.updateTaskComment(workflowId, comments);
      if (result?.success === false) {
        throw new Error(result.message ?? "Failed to save comment");
      }
      return result;
    },
    [client],
  );

  const runAction = useCallback(
    async (action: MyQueueTaskAction, jobTicketId: string, workflowId: number) => {
      const result = await client.taskAction(action, jobTicketId, workflowId);
      if (result?.success === false) {
        throw new Error(result.message ?? `${action} failed`);
      }
      return result;
    },
    [client],
  );

  return { loading, error, data, fetchDetails, updateComment, runAction };
}

export function useMyQueueTimeclock() {
  const adapter = useConnectionAdapter("myqueue");
  const client = useMyQueueClient();
  const [connected, setConnected] = useState<boolean>(adapter?.isConnected() ?? false);
  const [data, setData] = useState<MyQueueTimeclockData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [locations, setLocations] = useState<MyQueueLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(() => {
    const raw = readMigratedSessionPref(LOCATION_STORAGE_KEY);
    const n = raw ? Number(raw) : Number.NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState<string | null>(null);
  // After a user-initiated action we override `lastAction` with the action
  // verb we just sent — Engage's /timeclock/entries response sometimes
  // returns a lastAction value our classifier doesn't recognise, so the
  // user's explicit action is the authoritative source of truth until a
  // subsequent refresh returns something parseable.
  const [forcedLastAction, setForcedLastAction] = useState<MyQueueTimeclockAction | null>(null);

  useEffect(() => {
    if (!adapter) {
      setConnected(false);
      return;
    }
    setConnected(adapter.isConnected());
    return adapter.onTokenChange((t) => setConnected(!!t));
  }, [adapter]);

  useEffect(() => {
    try {
      if (selectedLocationId == null) {
        globalThis.sessionStorage?.removeItem(LOCATION_STORAGE_KEY);
      } else {
        globalThis.sessionStorage?.setItem(LOCATION_STORAGE_KEY, String(selectedLocationId));
      }
    } catch {
      // ignore
    }
  }, [selectedLocationId]);

  const fetchUserDetails = useCallback(async () => {
    setUserDetailsLoading(true);
    setUserDetailsError(null);
    try {
      const resp = await client.getUserDetails();
      if (!resp?.success) {
        throw new Error(resp?.message ?? "Failed to load user details");
      }
      const locs = resp.data?.locations ?? [];
      setLocations(locs);
      // If the persisted location no longer exists for this user, drop it
      // so we don't send a stale ID. Default to the first location when
      // nothing is selected yet.
      setSelectedLocationId((current) => {
        if (current != null && locs.some((l) => l.siteTimeclockLocationId === current)) {
          return current;
        }
        return locs[0]?.siteTimeclockLocationId ?? null;
      });
      reportMyQueueOk("load-user-details");
    } catch (e) {
      reportMyQueueError("load-user-details", e);
      setUserDetailsError(
        e instanceof Error ? e.message : "Failed to load user details",
      );
    } finally {
      setUserDetailsLoading(false);
    }
  }, [client]);

  const refresh = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await client.getTimeclockEntries();
      if (import.meta.env.DEV) {
        console.debug("[myqueue] /timeclock/entries response", resp);
      }
      if (!resp?.success) {
        throw new Error(resp?.message ?? "Failed to load timeclock");
      }
      const next = resp.data ?? null;
      setData(next);
      // Drop the optimistic override once the entries reflect the action
      // we just took (resolveState reads entries, not lastAction —
      // lastAction is unreliable, see timeclockState.ts).
      setForcedLastAction((forced) => {
        if (!forced) return forced;
        const newState = resolveState(next);
        const expected = stateAfterAction(forced);
        return newState === expected ? null : forced;
      });
      reportMyQueueOk("load-timeclock");
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load timeclock";
      setError(msg);
      reportMyQueueError("load-timeclock", e);
    } finally {
      setLoading(false);
    }
  }, [client, connected]);

  useEffect(() => {
    if (!connected) return;
    refresh().catch(() => undefined);
    fetchUserDetails().catch(() => undefined);
  }, [connected, refresh, fetchUserDetails]);

  const performAction = useCallback(
    async (action: MyQueueTimeclockAction) => {
      setActing(true);
      setError(null);
      try {
        // Every action (punchin/punchout/lunch/break/return) sends the same
        // { siteTimeclockLocationId } payload when a location is selected.
        const locId = selectedLocationId ?? undefined;
        const result = await client.postTimeclockAction(action, locId);
        if (result?.success === false) {
          throw new Error(result.message ?? `${action} failed`);
        }
        setForcedLastAction(action);
        await refresh();
        reportMyQueueOk("timeclock-action");
      } catch (e) {
        // Show a friendly, action-named message — never the raw transport
        // error (e.g. "MyQueue PUT /timeclock/punchin: 500"). The technical
        // detail still goes to telemetry via reportMyQueueError.
        setError(`${ACTION_LABELS[action]} failed. Please try again.`);
        reportMyQueueError("timeclock-action", e);
        throw e;
      } finally {
        setActing(false);
      }
    },
    [client, refresh, selectedLocationId],
  );

  const effectiveData = useMemo<MyQueueTimeclockData | null>(() => {
    if (!forcedLastAction) return data;
    return data ? { ...data, lastAction: forcedLastAction } : { lastAction: forcedLastAction };
  }, [data, forcedLastAction]);

  // The displayed clock state. Right after an action we trust it optimistically
  // (stateAfterAction) so the status flips immediately — e.g. clicking Lunch
  // shows "On Lunch" at once. resolveState reads entries first and would
  // otherwise keep showing the previous interval's state until the refresh
  // lands. Once refresh reconciles, forcedLastAction clears and we fall back to
  // the entries-authoritative resolveState.
  const state = useMemo(
    () => (forcedLastAction ? stateAfterAction(forcedLastAction) : resolveState(data)),
    [forcedLastAction, data],
  );

  return {
    connected,
    data: effectiveData,
    state,
    loading,
    error,
    acting,
    refresh,
    performAction,
    locations,
    selectedLocationId,
    setSelectedLocationId,
    userDetailsLoading,
    userDetailsError,
  };
}

export function useMyQueueQc() {
  const client = useMyQueueClient();
  const [qcInfo, setQcInfo] = useState<MyQueueQcInfoData | null>(null);
  const [prodTask, setProdTask] = useState<MyQueueProdTaskData | null>(null);
  // We keep the source production-task row separately from the GET-prod-task
  // response because the Pass/Fail submission needs the source row's id,
  // jobNo, and jobTask — the API response shape for /tasks/prod/{id} does
  // not consistently include these.
  const [activeProdTask, setActiveProdTask] =
    useState<MyQueueQcProdTask | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchQcInfo = useCallback(
    async (workflowId: number) => {
      setLoading(true);
      setError(null);
      setQcInfo(null);
      setProdTask(null);
      setActiveProdTask(null);
      try {
        const resp = await client.getQcInfo(workflowId);
        if (!resp?.success) {
          throw new Error(resp?.message ?? "Failed to load QC info");
        }
        setQcInfo(resp.data ?? null);
        reportMyQueueOk("load-qc-info");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load QC info";
        setError(msg);
        reportMyQueueError("load-qc-info", e);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const fetchProdTask = useCallback(
    async (pt: MyQueueQcProdTask) => {
      setLoading(true);
      setError(null);
      setProdTask(null);
      setActiveProdTask(pt);
      try {
        const resp = await client.getProdTask(pt.id);
        if (!resp?.success) {
          throw new Error(resp?.message ?? "Failed to load production task");
        }
        setProdTask(resp.data ?? null);
        reportMyQueueOk("load-production-task");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load production task";
        setError(msg);
        reportMyQueueError("load-production-task", e);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const submitQc = useCallback(
    async (pass: boolean) => {
      if (!activeProdTask || !qcInfo) return null;
      setSubmitting(true);
      setError(null);
      try {
        const resp = await client.setQcStatus(activeProdTask.id, {
          JobNo: qcInfo.jobNo ?? "",
          JobTask: activeProdTask.jobTask ?? qcInfo.jobTask ?? "",
          QcStatus: pass,
        });
        if (resp?.success === false) {
          throw new Error(resp.message ?? "QC submission failed");
        }
        reportMyQueueOk("qc-submit");
        return resp;
      } catch (e) {
        const raw = e instanceof Error ? e.message : "QC submission failed";
        // Surface the backend's actual reasons (e.g. "The task has not been
        // completed yet.") rather than the raw "POST …/qcstatus: 400 {…}".
        setError(friendlyMyQueueError(raw, "QC submission failed. Please try again."));
        reportMyQueueError("qc-submit", e);
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [client, activeProdTask, qcInfo],
  );

  const clearProdTask = useCallback(() => {
    setProdTask(null);
    setActiveProdTask(null);
  }, []);
  const clearQcInfo = useCallback(() => {
    setQcInfo(null);
    setProdTask(null);
    setActiveProdTask(null);
    setError(null);
  }, []);

  return {
    qcInfo,
    prodTask,
    activeProdTask,
    loading,
    error,
    submitting,
    fetchQcInfo,
    fetchProdTask,
    submitQc,
    clearProdTask,
    clearQcInfo,
  };
}

export function useMyQueueInvoices() {
  const client = useMyQueueClient();
  const [data, setData] = useState<MyQueueInvoiceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (jobNo: string) => {
      const trimmed = jobNo.trim();
      if (!trimmed) {
        setData(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const resp = await client.getInvoices(trimmed);
        if (!resp?.success) {
          throw new Error(resp?.message ?? "No invoice data");
        }
        setData(resp.data ?? null);
        reportMyQueueOk("load-invoices");
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load invoices";
        setError(msg);
        setData(null);
        reportMyQueueError("load-invoices", e);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  return { data, loading, error, search };
}
